import { useId } from "react";
import { GrafikCercevesi } from "./GrafikCercevesi";
import { GrafikAciklamasi, GrafikDesenleri, GrafikEkseni, HedefCizgisi } from "./grafik-svg";
import {
  gorunenEtiketler,
  guvenliToplam,
  hedefDegeri,
  kisalt,
  noktaEtiketi,
  olculen,
  sayiMetni,
  yOlcegi,
  zamanOzeti,
} from "./hesap";
import type { ZamanGrafigiProps } from "./tipler";

export interface CubukGrafikProps extends ZamanGrafigiProps {
  /** Çoklu seriler varsayılan olarak yığılır; false yan yana çizer. */
  yigili?: boolean;
}

export function CubukGrafik({
  baslik,
  noktalar,
  seriler,
  hedef,
  ozet,
  birim = "",
  bosMesaji,
  yigili = true,
}: CubukGrafikProps) {
  const kimlik = `cubuk-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const degerler = noktalar.map((nokta) =>
    seriler.map((seri) => olculen(nokta.degerler[seri.anahtar])),
  );
  const hedefler = noktalar.map((nokta) => hedefDegeri(nokta, hedef));
  const toplamlar = degerler.map((satir) =>
    guvenliToplam(satir.filter((deger): deger is number => deger !== null)),
  );
  const olcek = yOlcegi(
    [
      ...(yigili ? toplamlar : degerler.flat().filter((deger): deger is number => deger !== null)),
      ...hedefler.filter((deger): deger is number => deger !== null),
    ],
    20,
    172,
  );
  const adim = 296 / Math.max(noktalar.length, 1);
  const x = (sira: number) => 52 + adim * (sira + 0.5);
  const hedefVar = hedefler.some((deger) => deger !== null);
  const ozetMetni =
    ozet?.trim() || zamanOzeti(baslik, noktalar, seriler, birim, hedef, false, yigili);
  const gorunenler = gorunenEtiketler(noktalar.length, x);
  const bos = !degerler.some((satir) => satir.some((deger) => deger !== null));
  const sutunlar = [
    ...seriler.map((seri, sira) => `${sira + 1}. ${seri.etiket}`),
    ...(hedefVar ? ["Hedef"] : []),
  ];
  return (
    <GrafikCercevesi
      baslik={baslik}
      ozet={ozetMetni}
      bos={bos}
      bosMesaji={bosMesaji}
      basliklar={sutunlar}
      satirlar={noktalar.map((nokta, sira) => ({
        anahtar: String(sira),
        etiket: noktaEtiketi(nokta, sira),
        hucreler: [
          ...(degerler[sira] ?? []).map((deger) => sayiMetni(deger, birim)),
          ...(hedefVar ? [sayiMetni(hedefler[sira], birim)] : []),
        ],
      }))}
      aciklama={<GrafikAciklamasi seriler={seriler} hedefVar={hedefVar} birim={birim} />}
    >
      <svg
        role="img"
        aria-label={ozetMetni}
        viewBox="0 0 360 220"
        width="100%"
        // 480 üstü: viewBox oranla büyüyor, geniş kapta yazılar dev oluyordu (ölçüldü, dalga 3).
        style={{ display: "block", height: "auto", maxWidth: 480 }}
      >
        <title>{ozetMetni}</title>
        <GrafikDesenleri kimlik={kimlik} seriler={seriler} />
        <GrafikEkseni enAz={olcek.enAz} enCok={olcek.enCok} konum={olcek.konum} birim={birim}>
          {noktalar.map((nokta, sira) =>
            gorunenler.has(sira) ? (
              <text key={sira} x={x(sira)} y="196" textAnchor="middle">
                <title>{noktaEtiketi(nokta, sira)}</title>
                {kisalt(noktaEtiketi(nokta, sira), 8)}
              </text>
            ) : null,
          )}
        </GrafikEkseni>
        {degerler.map((satir, sira) => {
          let toplam = 0;
          return (
            <g key={sira}>
              {satir.map((deger, seriSirasi) => {
                const seri = seriler[seriSirasi];
                if (deger === null || !seri) return null;
                const taban = yigili ? toplam : 0;
                toplam = guvenliToplam([toplam, deger]);
                const tepe = yigili ? toplam : deger;
                const genislik = (adim * 0.72) / (yigili ? 1 : Math.max(seriler.length, 1));
                const sol = x(sira) - adim * 0.36 + (yigili ? 0 : seriSirasi * genislik);
                const yukseklik = Math.max(0, olcek.konum(taban) - olcek.konum(tepe));
                return (
                  <g key={seriSirasi}>
                    <rect
                      data-testid="cubuk"
                      x={sol}
                      y={olcek.konum(tepe)}
                      width={genislik}
                      height={yukseklik}
                      fill={`url(#${kimlik}-${seriSirasi})`}
                    >
                      <title>
                        {noktaEtiketi(noktalar[sira] ?? {}, sira)} — {seri.etiket}:{" "}
                        {sayiMetni(deger, birim)}
                      </title>
                    </rect>
                    {seriler.length > 1 && genislik >= 20 && yukseklik >= 18 && (
                      <text
                        x={sol + genislik / 2}
                        y={olcek.konum(tepe) + yukseklik / 2 + 4}
                        textAnchor="middle"
                        fontSize="12"
                        fontWeight="600"
                        fill="var(--color-notr-900)"
                        stroke="var(--color-notr-0)"
                        strokeWidth="3"
                        paintOrder="stroke"
                      >
                        {seriSirasi + 1}
                      </text>
                    )}
                    {deger === 0 && (
                      <line
                        x1={sol}
                        x2={sol + genislik}
                        y1="172"
                        y2="172"
                        stroke={`url(#${kimlik}-${seriSirasi})`}
                        strokeWidth="2"
                      />
                    )}
                  </g>
                );
              })}
              {satir.some((deger) => deger === null) && (
                <text
                  x={x(sira)}
                  y="214"
                  textAnchor="middle"
                  fontSize="12"
                  fill="var(--color-notr-600)"
                >
                  <title>Eksik ölçüm; toplam kısmi olabilir</title>?
                </text>
              )}
            </g>
          );
        })}
        <HedefCizgisi hedefler={hedefler} x={x} y={olcek.konum} birim={birim} />
      </svg>
      {degerler.some((satir) => satir.some((deger) => deger === null)) && (
        <p className="text-notr-600 text-sm">? Eksik ölçüm; toplam kısmi olabilir.</p>
      )}
    </GrafikCercevesi>
  );
}
